import { Button, ErrorAlert, FormError, Notice, PasswordInput, linkStyles, useTextForm } from '@lumlab/platform-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { authClient } from '../app/auth.ts';
import { AuthScreen, authErrorCode, takeFragmentParameters } from '../app/AuthScreen.tsx';

const minimumPasswordLength = 12;

export function ResetPasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [token] = useState(() => takeFragmentParameters().get('token') ?? '');
    const [error, setError] = useState<{ code: string } | null>(null);
    const [pending, setPending] = useState(false);
    const form = useTextForm({
        initialValues: { password: '', confirmation: '' },
        validate: (values) => ({
            password:
                values.password.length < minimumPasswordLength ? t('platform.auth.resetPassword.tooShort') : undefined,
            confirmation:
                values.confirmation !== values.password ? t('platform.auth.resetPassword.mismatch') : undefined,
        }),
    });

    const submit = form.onSubmit(async ({ password }) => {
        setPending(true);
        setError(null);
        try {
            const result = await authClient.resetPassword({ newPassword: password, token });
            if (result.error) setError({ code: authErrorCode(result.error) });
            else void navigate('/sign-in', { replace: true, state: { notice: 'passwordReset' } });
        } catch {
            setError({ code: 'internal' });
        } finally {
            setPending(false);
        }
    });

    return (
        <AuthScreen title={t('platform.auth.resetPassword.title')}>
            {token ? (
                <form onSubmit={submit} className="flex flex-col gap-4">
                    <FormError message={form.formError} />
                    {error && <ErrorAlert error={error} />}
                    <PasswordInput
                        autoComplete="new-password"
                        required
                        label={t('platform.auth.resetPassword.password')}
                        description={t('platform.auth.resetPassword.hint')}
                        {...form.getInputProps('password')}
                    />
                    <PasswordInput
                        autoComplete="new-password"
                        required
                        label={t('platform.auth.resetPassword.confirmation')}
                        {...form.getInputProps('confirmation')}
                    />
                    <Button type="submit" loading={pending}>
                        {t('platform.auth.resetPassword.submit')}
                    </Button>
                </form>
            ) : (
                <Notice tone="warning">{t('platform.auth.resetPassword.missingToken')}</Notice>
            )}
            {(!token || error?.code === 'invalid_link') && (
                <Link to="/forgot-password" className={`text-sm ${linkStyles}`}>
                    {t('platform.auth.resetPassword.requestNew')}
                </Link>
            )}
        </AuthScreen>
    );
}
